import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
} from "@mui/material";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { useSelector } from "react-redux";
import { getAccessToken } from "../../../utils/tokenManager";
import { getAuthToken } from "../../../utils/getAuthToken";
import { disconnectInstagram } from "../../../services/instagramApi";
import { disconnectFacebook } from "../../../services/facebookApi";

const CHANNELS = {
  instagram: { label: "Instagram", disconnect: disconnectInstagram },
  facebook: { label: "Facebook Messenger", disconnect: disconnectFacebook },
};

/**
 * "Disconnect" action for the Instagram / Facebook Messenger integrations. Same confirmation
 * flow as DisconnectShopifyButton. Disconnecting removes the connection server-side, so
 * messages from that channel stop arriving until it is connected again.
 */
const DisconnectSocialButton = ({ channel, onDisconnected, variant = "outlined", size = "small", sx }) => {
  const { label, disconnect } = CHANNELS[channel];
  const workspaceId = useSelector(
    (s) => s.workspace?.currentWorkspace?._id || s.auth?.user?.workspaceId
  );

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleConfirmDisconnect = async () => {
    if (!workspaceId) return;
    setDisconnecting(true);
    setErrorMessage("");
    try {
      let token = getAccessToken();
      if (!token) token = await getAuthToken();
      await disconnect(workspaceId, token);
      setConfirmOpen(false);
      onDisconnected?.();
    } catch (err) {
      setErrorMessage(err.response?.data?.message || err.message || `Failed to disconnect ${label}.`);
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        color="error"
        size={size}
        startIcon={<LinkOffIcon />}
        onClick={() => setConfirmOpen(true)}
        sx={{ textTransform: "none", width: "fit-content", ...sx }}
      >
        Disconnect
      </Button>

      <Dialog open={confirmOpen} onClose={() => (disconnecting ? null : setConfirmOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: "1.4rem", fontWeight: 700, pt: 3, px: 3 }}>
          Disconnect {label}
        </DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to disconnect {label}? You will stop receiving and sending {label} messages
            from this workspace, and workflows using it will stop working.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Existing chats and contacts are kept. You can connect again at any time.
          </Typography>
          {errorMessage && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={disconnecting}
            size="large"
            sx={{ textTransform: "none", fontSize: "1rem" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDisconnect}
            disabled={disconnecting}
            size="large"
            sx={{ textTransform: "none", fontSize: "1rem", px: 3 }}
          >
            {disconnecting ? "Disconnecting..." : "Yes, Disconnect"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DisconnectSocialButton;
